// src/components/messaging/ConversationList.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';

const ConversationList = ({ 
  conversations, 
  onRefresh, 
  loading, 
  currentUserId 
}) => {
  const navigate = useNavigate();

  // Handle conversation click - navigate to individual chat
  const handleConversationClick = (conversationId) => {
    navigate(`/messages/${conversationId}`);
  };

  // Format timestamp for last message
  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp instanceof Date 
      ? timestamp 
      : new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
    
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      // Today: show time
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInDays === 1) {
      // Yesterday
      return 'Yesterday';
    } else if (diffInDays < 7) {
      // Within the last week: show day name
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      // More than a week ago: show date
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };
  
  // Get conversation partner name
const getPartnerName = (conversation) => {
  if (!conversation || !currentUserId) {
    console.warn('Missing conversation or currentUserId in getPartnerName');
    return 'Unknown';
  }
  
  // Find the other participant ID (not current user)
  const partnerId = conversation.participants.find(id => id !== currentUserId);
  
  if (!partnerId) {
    console.warn('Could not find partner ID in conversation', conversation.id);
    return 'Unknown';
  }
  
  console.log('ConversationList - Getting partner name for:', partnerId);
  console.log('ConversationList - Members:', conversation.members);
  
  // Try to get name in order of preference:
  // 1. members object
  if (conversation.members && conversation.members[partnerId]) {
    const name = conversation.members[partnerId].displayName;
    if (name) return name;
  }
  
  // 2. participantMap
  if (conversation.participantMap && conversation.participantMap[partnerId]) {
    const name = conversation.participantMap[partnerId].displayName;
    if (name) return name;
  }
  
  // 3. If lastSenderId matches partnerId, try lastSenderName
  if (conversation.lastSenderId === partnerId && conversation.lastSenderName) {
    return conversation.lastSenderName;
  }
  
  return 'Unknown';
};
  
  // Get conversation partner initials for avatar
  const getPartnerInitials = (conversation) => {
    const name = getPartnerName(conversation);
    if (name === 'Unknown') return '?';
    
    // Get first letters of first and last name
    const nameParts = name.split(' ');
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    } else {
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    }
  };
  
  // Check if message is unread
  const isUnread = (conversation) => {
    if (!conversation || !currentUserId) return false;
    
    return (
      conversation.lastSenderId !== currentUserId && 
      !conversation.readBy?.includes(currentUserId)
    );
  };
  
  // Truncate last message if too long
  const truncateMessage = (message, maxLength = 40) => {
    if (!message) return '';
    if (message.length <= maxLength) return message;
    
    return message.substring(0, maxLength) + '...';
  };
  
  return (
    <div className="conversation-list">
      {loading ? (
        <div className="loading-conversations">
          <div className="loading-spinner"></div>
          <p>Loading conversations...</p>
        </div>
      ) : conversations.length === 0 ? (
        <div className="empty-conversations">
          <p>No conversations yet</p>
          <button onClick={onRefresh} className="refresh-button">
            Refresh
          </button>
        </div>
      ) : (
        <>
          <button onClick={onRefresh} className="refresh-button">
            Refresh
          </button>
          
          <ul className="conversations">
            {conversations.map(conversation => (
              <li 
                key={conversation.id} 
                className={`conversation-item ${isUnread(conversation) ? 'unread' : ''}`}
                onClick={() => handleConversationClick(conversation.id)}
              >
                <div className="conversation-avatar">
                  {getPartnerInitials(conversation)}
                </div>
                
                <div className="conversation-info">
                  <div className="conversation-header">
                    <span className="conversation-name">
                      {getPartnerName(conversation)}
                    </span>
                    <span className="conversation-time">
                      {formatLastMessageTime(conversation.lastMessageTimestamp)}
                    </span>
                  </div>
                  
                  <div className="conversation-message">
                    {isUnread(conversation) && (
                      <span className="unread-indicator">●</span>
                    )}
                    <span className="message-preview">
                      {truncateMessage(conversation.lastMessage)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default ConversationList;